/* eslint-disable no-restricted-imports */
import * as React from 'react';

import { Heading, Text } from '@/components/ui';
import { WM_FLAT_SURFACE_CLASSNAME } from '@/components/ui/surfaceStyles';

const HEADING_VARIANTS = [
    { variant: 'display', description: 'Page-level hero titles.', sample: 'Energy that adds up' },
    { variant: 'title-lg', description: 'Section titles — catalog family headers, tab page titles.', sample: 'Components' },
    { variant: 'title', description: 'Card and modal titles. Default heading variant.', sample: 'Meter overview' },
    { variant: 'title-sm', description: 'Subsection titles inside dense UI.', sample: 'Reading breakdown' },
    { variant: 'title-xs', description: 'Inline-label headings used inside tight widgets.', sample: 'Status' },
    { variant: 'metric', description: 'KPI tiles and dashboard metrics.', sample: '1,248.3 kWh' }
];

const TEXT_VARIANTS = [
    { variant: 'body', description: 'Default paragraph text.', sample: 'The meter reports hourly consumption with a small margin of error.' },
    { variant: 'body-sm', description: 'Compact paragraph text — sidebars, help copy.', sample: 'Refresh the report if the latest reading looks stale.' },
    { variant: 'control', description: 'Labels on interactive controls (buttons, tabs, form fields).', sample: 'Save changes' },
    { variant: 'label', description: 'Static labels next to values inside tight tables.', sample: 'Meter ID' },
    { variant: 'caption', description: 'Dense captions — chart axes, sidebar metadata.', sample: 'Past 24 hours' },
    { variant: 'detail', description: 'Emphasised caption — list metadata, row secondaries.', sample: '3 alerts unresolved' },
    { variant: 'overline', description: 'Section eyebrow labels above a title.', sample: 'Recent activity' },
    { variant: 'overline-micro', description: 'Extra-small eyebrow for very dense UI.', sample: 'Legend' },
    { variant: 'micro', description: 'Micro chrome text — schedule grid headers.', sample: '00 15 30 45' },
    { variant: 'micro-8', description: 'Smallest tick text — sub-hour schedule grid markers.', sample: 'q1 q2 q3' }
];

const VariantRow = ({ label, description, children }) => (
    <div className="tw-grid tw-grid-cols-1 tw-gap-2 tw-border-b tw-border-border tw-py-4 last:tw-border-b-0 md:tw-grid-cols-[14rem_1fr] md:tw-gap-6">
        <div className="tw-space-y-1">
            <Text as="p" variant="control">{label}</Text>
            <Text as="p" tone="muted" variant="caption">{description}</Text>
        </div>
        <div className="tw-flex tw-items-center">
            {children}
        </div>
    </div>
);

export default function TypographyFoundation() {
    return (
        <div className="tw-space-y-8">
            <header className="tw-space-y-2">
                <Text tone="muted" variant="overline">Foundation</Text>
                <Heading as="h2" variant="title-lg">Typography</Heading>
                <Text as="p" tone="muted" variant="body">
                    Two primitives carry every text slot in the app. <code>Heading</code> for
                    titles, <code>Text</code> for everything else. Variants encode size, weight
                    and line-height together so you never mix Tailwind typography utilities in
                    application code.
                </Text>
            </header>

            <section className={`${WM_FLAT_SURFACE_CLASSNAME} tw-rounded-3xl tw-p-6 lg:tw-p-8`}>
                <Heading as="h3" variant="title">Heading variants</Heading>
                <Text tone="muted" variant="body-sm">
                    <code>&lt;Heading variant=&quot;title&quot; /&gt;</code> — defaults to
                    <code> h2</code>, override with <code>as</code>.
                </Text>
                <div className="tw-mt-4">
                    {HEADING_VARIANTS.map(({ variant, description, sample }) => (
                        <VariantRow key={variant} label={variant} description={description}>
                            <Heading variant={variant}>{sample}</Heading>
                        </VariantRow>
                    ))}
                </div>
            </section>

            <section className={`${WM_FLAT_SURFACE_CLASSNAME} tw-rounded-3xl tw-p-6 lg:tw-p-8`}>
                <Heading as="h3" variant="title">Text variants</Heading>
                <Text tone="muted" variant="body-sm">
                    <code>&lt;Text variant=&quot;body&quot; /&gt;</code> — defaults to
                    <code> p</code>, override with <code>as</code>.
                </Text>
                <div className="tw-mt-4">
                    {TEXT_VARIANTS.map(({ variant, description, sample }) => (
                        <VariantRow key={variant} label={variant} description={description}>
                            <Text variant={variant}>{sample}</Text>
                        </VariantRow>
                    ))}
                </div>
            </section>

            <section className={`${WM_FLAT_SURFACE_CLASSNAME} tw-rounded-3xl tw-p-6 lg:tw-p-8`}>
                <Heading as="h3" variant="title">Tones</Heading>
                <Text tone="muted" variant="body-sm">
                    Both <code>Heading</code> and <code>Text</code> accept a <code>tone</code>
                    prop — <code>foreground</code> (default), <code>muted</code>, <code>background</code>
                    (for on-brand surfaces), and <code>inherit</code>.
                </Text>
                <div className="tw-mt-4 tw-space-y-3">
                    <Text>Default foreground — standard paragraph text.</Text>
                    <Text tone="muted">Muted — use for secondary information.</Text>
                    <div className="tw-rounded-2xl tw-bg-primary tw-p-4">
                        <Text tone="background">Background tone — used on brand-filled surfaces.</Text>
                    </div>
                </div>
            </section>
        </div>
    );
}
